/**
 * `/checkout/redirect` — plan 030 signup intercept.
 *
 * Called after the mnemonic-reveal screen when the signup URL carried
 * `intent=paid`. Kicks off a Stripe Checkout session with card-required
 * + 14-day trial and redirects the browser to Stripe. Renders a small
 * "Redirecting…" panel while the CP round-trip happens, plus the
 * escape-hatch link so a parent can bail out to a card-free trial.
 *
 * If checkoutIntent isn't set (someone hit this URL directly), we bounce
 * to the dashboard — nothing meaningful to do here without the cadence.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Card } from '../components/Card';
import { startCheckout } from '../data/billing';
import { checkoutIntent, clearCheckoutIntent } from '../state/checkout-intent';

const TRIAL_DAYS = 14;

export function CheckoutRedirectRoute() {
  const location = useLocation();
  const kicked = useRef(false);
  const [err, setErr] = useState<string | null>(null);

  const intent = checkoutIntent.value;

  useEffect(() => {
    if (intent === null || !intent.paid) {
      location.route('/dashboard');
      return;
    }
    if (kicked.current) return;
    kicked.current = true;
    void (async () => {
      try {
        await startCheckout(intent.cadence, {
          trialDays: TRIAL_DAYS,
          successPath: '/checkout/success',
          cancelPath: '/checkout/cancel',
        });
        // startCheckout does a full-page redirect. If control returns
        // here, it means the CP call succeeded but the location swap
        // hasn't landed yet — nothing else to do.
      } catch (e) {
        setErr((e as Error).message);
      }
    })();
  }, [intent, location]);

  function onSkip(): void {
    // Preserve the "signup made it this far" state — clear the paid
    // intent so any refresh doesn't re-fire the checkout attempt, then
    // land on the dashboard where the BillingBanner + settings pill
    // already surface the trial-without-card state.
    clearCheckoutIntent();
    location.route('/dashboard');
  }

  return (
    <main class="flex min-h-screen items-center justify-center p-4">
      <Card class="w-full max-w-md space-y-4">
        <h1 class="text-xl font-semibold">Opening Stripe checkout…</h1>
        <p class="text-sm text-surface-fg/70">
          You&apos;ll be able to enter a card in a moment. Backstop won&apos;t
          charge you for the first 14 days.
        </p>
        {err !== null && (
          <p class="text-sm text-danger-500" role="alert">
            {err}
          </p>
        )}
        <div class="border-t border-line pt-3">
          <button
            type="button"
            class="text-sm text-surface-muted underline hover:text-signal"
            onClick={onSkip}
          >
            Start your 14-day free trial without a card
          </button>
          <p class="mt-1 text-xs text-surface-muted">
            You can add a card any time in Settings.
          </p>
        </div>
      </Card>
    </main>
  );
}
